import { Input } from 'antd';
import { useGetCliniciansList } from 'lib/hooks/tanstack/use-GetCliniciansList';
import { useGetClinicAllInfo } from 'lib/hooks/tanstack/use-getClinicInfo';
import { uniqBy } from 'lodash';
import { useMemo } from 'react';
import {
  BASE_USER_ADD_QUESTIONS,
  FormQuestion
} from '../../../../types/form-questions';

export const EXTRA_PATIENT_QUESTIONS: FormQuestion[] = [
  {
    type: 'custom',
    name: 'phoneNumber',
    label: 'Phone Number',
    rules: [
      { required: false },
      {
        pattern: /^\+[1-9]\d{1,14}$/,
        message: `The phone number should be in E.164 format. The number should start with '+' and have no dashes. e.g: '+11234567890'`
      }
    ],
    component: props => <Input {...props} disabled={props.disabled} />
  }
];

/**
 * Hook to generate the questions for patient add and edit actions
 * @param clinicId
 * @returns
 */
export function useResolvePatientUserAddQuestions(clinicId: string) {
  const cliniciansQuery = useGetCliniciansList(clinicId);
  const clinicInfo = useGetClinicAllInfo(clinicId);
  const FEATURE_FLAG_ASSESSMENT_BASELINE =
    !!clinicInfo.clinicInfo?.clinic.crx_settings?.FEATURE_FLAGS
      .ASSESSMENT_BASELINE;
  const patientUserAddQuestions = useMemo<FormQuestion[]>(() => {
    const clinicians =
      cliniciansQuery.data?.pages.flatMap(_ => _.data.data) ?? [];

    const clinicianSelectionOptions = uniqBy(
      clinicians.map(_ => ({
        label: `${_.displayName} - ${_.id}`,
        value: _.id
      })),
      _ => _.value
    );

    return [
      ...BASE_USER_ADD_QUESTIONS,
      ...EXTRA_PATIENT_QUESTIONS,
      {
        type: 'select',
        name: 'assessmentType',
        label: 'Assessment type',
        rules: [
          { required: true },
          { type: 'enum', enum: ['baseline', 'post-injury'] }
        ],
        options: [
          ...(FEATURE_FLAG_ASSESSMENT_BASELINE
            ? [
                {
                  label: 'BASELINE',
                  value: 'baseline'
                }
              ]
            : []),
          {
            label: 'POST-INJURY',
            value: 'post-injury'
          }
        ]
      },
      {
        type: 'select',
        name: 'clinicianId',
        label: 'Clinician',
        rules: [
          { required: true },
          { type: 'enum', enum: clinicianSelectionOptions.map(_ => _.value) }
        ],
        loading: clinicians.length === 0,
        disabled: clinicians.length === 0,
        options: clinicianSelectionOptions
      }
    ];
  }, [cliniciansQuery.data, FEATURE_FLAG_ASSESSMENT_BASELINE]);

  return patientUserAddQuestions;
}
